/** @format */
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    Inject,
    input,
    linkedSignal,
    OnDestroy,
    Signal,
    ViewEncapsulation,
    inject,
} from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import {
    AnyCourseUnitRule,
    CourseUnit,
    CourseUnitResultItem,
    CustomCourseUnitAttainment,
    CustomStudyDraft,
    EntityWithRule,
    OtmId,
} from 'common-typescript/types';
import { Subject } from 'rxjs';
import { PLAN_ACTIONS_SERVICE_INJECTION_TOKEN, PlanActionsService } from '../../../plan/plan-actions-service/plan-actions.service';
import { ReadMoreModalService } from '../../../read-more-ng/read-more-modal.service';
import { ReadMoreValues } from '../../../read-more-ng/read-more-ng-modal/read-more-ng-modal.component';
import { getRuleValidationResultFromState } from '../../../service/rule-validation-result-in-state';
import { PlanStructureContext } from '../../plan-structure-context';

@Component({
    selector: 'sis-plan-structure-any-course-unit-rule',
    templateUrl: './plan-structure-any-course-unit-rule.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class PlanStructureAnyCourseUnitRuleComponent implements OnDestroy {
    readonly rule = input.required<AnyCourseUnitRule>();
    readonly parentModule = input.required<EntityWithRule>();
    readonly ruleDepthLevel = input.required<number>();
    readonly headingLevel = input.required<number>();
    readonly selectionUIState = input.required<'ACTIVE' | 'DISABLED' | 'SELECTABLE'>();
    readonly currentUniversityOrgId = input.required<OtmId>();
    readonly crossStudyFeaturesEnabled = input.required<boolean>();
    readonly cooperationNetworkIds = input.required<OtmId[]>();
    readonly curriculumPeriodIds = input.required<OtmId[]>();

    private readonly ruleValidationResult = computed(() =>
        getRuleValidationResultFromState(this.planStructureContext.planStateObject(), this.parentModule(), this.rule()),
    );

    // A selection stays shown after it leaves the plan, so that it can be selected again
    protected readonly courseUnits = this.shownSelections(() =>
        Object.values(this.ruleValidationResult()?.selectedCourseUnitsById ?? {}).filter(
            cu => !this.planStructureContext.planStateObject().courseUnits[cu.id]?.isInPlanAsSubstitute,
        ),
    );

    protected readonly customCourseUnitAttainments = this.shownSelections(() =>
        Object.values(this.ruleValidationResult()?.selectedCustomCourseUnitAttainmentsById ?? {}),
    );

    protected readonly customStudyDrafts = this.shownSelections(() =>
        Object.values(this.ruleValidationResult()?.selectedCustomStudyDraftsById ?? {}),
    );

    protected readonly courseUnitInPlanMap = computed(() =>
        this.inPlanMap(this.courseUnits(), cu => this.planStructureContext.planStateObject().courseUnits[cu.id]?.isInPlan ?? false),
    );

    protected readonly customCourseUnitAttainmentInPlanMap = computed(() =>
        this.inPlanMap(
            this.customCourseUnitAttainments(),
            ccua => this.planStructureContext.planStateObject().customCourseUnitAttainments[ccua.id]?.isInPlan ?? false,
        ),
    );

    protected readonly customStudyDraftInPlanMap = computed(() =>
        this.inPlanMap(this.customStudyDrafts(), csd => !!this.planStructureContext.planData().customStudyDraftsById[csd.id]),
    );

    protected readonly excludedCourseUnitIds = computed(
        () =>
            new Set<string>([...Object.keys(this.planStructureContext.planData().courseUnitsById), ...this.courseUnits().map(cu => cu.id)]),
    );

    private destroy$ = new Subject<void>();

    private readonly planStructureContext = inject(PlanStructureContext);

    constructor(
        private translocoService: TranslocoService,
        private readMoreModalService: ReadMoreModalService,
        @Inject(PLAN_ACTIONS_SERVICE_INJECTION_TOKEN) private planActionsService: PlanActionsService,
    ) {}

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    handleCUSelectToggle(courseUnit: CourseUnit): void {
        const parentModule = this.parentModule();
        if (!this.courseUnitInPlanMap()[courseUnit.id]) {
            this.planActionsService?.selectCourseUnit(courseUnit, parentModule);
        } else {
            this.planActionsService?.removeCourseUnit(courseUnit, parentModule);
        }
    }

    handleCCUASelectToggle(customCourseUnitAttainment: CustomCourseUnitAttainment): void {
        const parentModule = this.parentModule();
        if (!this.customCourseUnitAttainmentInPlanMap()[customCourseUnitAttainment.id]) {
            this.planActionsService?.selectCustomCourseUnitAttainment(customCourseUnitAttainment, parentModule);
        } else {
            this.planActionsService?.removeCustomCourseUnitAttainmentById(customCourseUnitAttainment.id, parentModule);
        }
    }

    handleCSDSelectToggle(customStudyDraft: CustomStudyDraft): void {
        const parentModule = this.parentModule();
        if (!this.customStudyDraftInPlanMap()[customStudyDraft.id]) {
            this.planActionsService?.addCustomStudyDraft(customStudyDraft, parentModule);
        } else {
            this.planActionsService?.removeCustomStudyDraft(customStudyDraft, parentModule);
        }
    }

    openAddStudyDraftModal() {
        this.planActionsService.openCustomStudyDraftCreationModal(this.parentModule());
    }

    openReadMoreAboutStudyDraftModal() {
        this.readMoreModalService.open(this.getReadMoreOptions());
    }

    getReadMoreOptions(): ReadMoreValues {
        return {
            options: {
                contentHtml: null,
                contentTranslateKey: 'PLAN_EDIT.SELECTION_MODAL.ANY_COURSE_UNIT_RULE.CUSTOM_STUDY_DRAFT_READ_MORE_DESCRIPTION',
                contentTranslateParameters: null,
                title: this.translocoService.translate('PLAN_EDIT.SELECTION_MODAL.ANY_COURSE_UNIT_RULE.CUSTOM_STUDY_DRAFT_READ_MORE_TITLE'),
            },
        };
    }

    updateSelected(courseUnit: CourseUnitResultItem): void {
        if (courseUnit) {
            this.planActionsService?.selectCourseUnitByGroupId(courseUnit.groupId, this.parentModule());
        }
    }

    private shownSelections<T extends { id: OtmId }>(selectionsInPlan: () => T[]): Signal<T[]> {
        return linkedSignal<T[], T[]>({
            source: selectionsInPlan,
            computation: (newSelections, previous) => this.concatAndClean(previous?.value ?? [], newSelections),
        });
    }

    private inPlanMap<T extends { id: OtmId }>(selections: T[], isInPlan: (selection: T) => boolean): { [id: string]: boolean } {
        return Object.fromEntries(selections.map(selection => [selection.id, isInPlan(selection)]));
    }

    /** The shown selections followed by the new ones, each id once, in the place where it first appears */
    private concatAndClean<T extends { id: OtmId }>(visibleSelections: T[], newSelections: T[]): T[] {
        const selectionsById = new Map<OtmId, T>();
        [...visibleSelections, ...newSelections].filter(Boolean).forEach(selection => {
            if (!selectionsById.has(selection.id)) {
                selectionsById.set(selection.id, selection);
            }
        });
        return [...selectionsById.values()];
    }
}
